import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ArrowRight, Check, Save } from 'lucide-react';
import { toast } from 'sonner';
import { useProfile } from './ProfileContext';
import { profileSteps } from './ProfileSteps';

// Map each step index (currentStep-1) to the key in profileData
const stepSectionKeys = [
  'basicInfo',
  'financialGoals',
  'riskAssessment',
  'currentPortfolio',
  'taxInfo',
  'investmentPreferences',
  null // Profile Overview doesn't have a data section to save
];

const ProfileNavigation: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, profileData, updateProfileData, flushCurrentStepData } = useProfile();

  const handleNext = async (event: React.MouseEvent) => {
    try {
      event.preventDefault();
      console.log('Next button clicked, current step:', currentStep);
      
      // Immediately update the UI to show we're moving to the next step
      if (currentStep < profileSteps.length) {
        setCurrentStep(currentStep + 1);
        window.scrollTo(0, 0);
      }
      
      // Then try to save data in the background
      const currentSection = stepSectionKeys[currentStep - 1];
      try {
        // 1. Flush pending edits from step
        await flushCurrentStepData();
        // 2. Get latest data and update Supabase
        if (currentSection) {
          const currentData = profileData[currentSection];
          await updateProfileData(currentSection, currentData, false);
        }
      } catch (saveError) {
        console.error('Error saving data:', saveError);
        // Show error but don't revert the navigation
        toast.error('Data was not saved, but you can continue with your profile setup');
      }
    } catch (error) {
      console.error('Error in handleNext:', error);
      toast.error('Failed to proceed to next step');
    }
  };

  const handlePrevious = (event: React.MouseEvent) => {
    event.preventDefault();
    console.log('Previous button clicked');
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleSaveAndExit = async (event: React.MouseEvent) => {
    try {
      event.preventDefault();
      console.log('Save and Exit button clicked');
      
      // Show success message immediately for better UX
      toast.success("Progress saved! You can continue later.", {
        duration: 3000,
      });
      
      // Navigate away quickly
      setTimeout(() => {
        navigate('/dashboard');
      }, 500);
      
      // Then try to save in the background
      try {
        const currentSection = stepSectionKeys[currentStep - 1];
        await flushCurrentStepData();
        if (currentSection) {
          const currentData = profileData[currentSection];
          await updateProfileData(currentSection, currentData, false);
        }
      } catch (saveError) {
        console.error('Error silently saving data:', saveError);
        // We're already navigating away, so we don't show this error
      }
    } catch (error) {
      console.error('Error in handleSaveAndExit:', error);
      toast.error('Failed to save progress');
    }
  };

  const handleComplete = async (event: React.MouseEvent) => {
    try {
      event.preventDefault();
      console.log('Complete button clicked');
      
      // Show success message immediately for better UX
      toast.success("Profile setup complete!", {
        duration: 3000,
      });
      
      // Navigate away quickly
      setTimeout(() => {
        navigate('/dashboard');
      }, 500);
      
      // Then try to save in the background
      try {
        const currentSection = stepSectionKeys[currentStep - 1];
        await flushCurrentStepData();
        if (currentSection) {
          const currentData = profileData[currentSection];
          await updateProfileData(currentSection, currentData, false);
        }
      } catch (saveError) {
        console.error('Error silently saving data:', saveError);
        // We're already navigating away, so we don't show this error
      }
    } catch (error) {
      console.error('Error in handleComplete:', error);
      toast.error('Failed to complete profile setup');
    }
  };

  const isLastStep = currentStep === profileSteps.length;

  return (
    <div className="flex justify-between mt-8">
      <div>
        {currentStep > 1 && (
          <Button 
            onClick={handlePrevious}
            variant="outline" 
            className="border-[#9E7C47]/50 text-[#9E7C47]"
            type="button"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Previous
          </Button>
        )}
      </div>
      <div className="flex gap-3">
        <Button 
          onClick={handleSaveAndExit}
          variant="outline" 
          className="border-white/20 text-white/70 hover:bg-white/5"
          type="button"
        >
          <Save className="h-4 w-4 mr-2" />
          Save &amp; Exit
        </Button>
        {isLastStep ? (
          <Button 
            onClick={handleComplete}
            className="bg-[#9E7C47] hover:bg-[#9E7C47]/90"
            type="button"
          >
            <Check className="h-4 w-4 mr-2" />
            Complete Setup
          </Button>
        ) : (
          <Button 
            onClick={handleNext}
            className="bg-[#9E7C47] hover:bg-[#9E7C47]/90"
            type="button"
          >
            Next
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default ProfileNavigation;
